import { useState, useEffect } from 'react';
import { useUIStore } from '../store/uiStore';
import { notificacionService, Notificacion } from '../services/notificacionService';
import { Bell, Check, X, Clock, Heart, Award, Target } from 'lucide-react';

// D3: página de notificaciones persistida (backend + BD), ya no useState local.
export default function NotificacionesPage() {
  const { addToast } = useUIStore();
  const [notifications, setNotifications] = useState<Notificacion[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadNotificaciones();
  }, []);

  const loadNotificaciones = async () => {
    try {
      const data = await notificacionService.listar();
      setNotifications(data);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudieron cargar las notificaciones',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (id: number) => {
    try {
      await notificacionService.marcarLeida(id);
      setNotifications(notifications.map((n) => (n.id === id ? { ...n, leida: true } : n)));
    } catch (error) {
      console.error('Error al marcar notificación:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificacionService.marcarTodas();
      setNotifications(notifications.map((n) => ({ ...n, leida: true })));
      addToast({
        type: 'success',
        title: 'Notificaciones leídas',
        message: 'Todas las notificaciones han sido marcadas como leídas',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudieron marcar las notificaciones',
      });
    }
  };

  const deleteNotification = async (id: number) => {
    try {
      await notificacionService.eliminar(id);
      setNotifications(notifications.filter((n) => n.id !== id));
    } catch (error) {
      console.error('Error al eliminar notificación:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'meta': return Target;
      case 'ejercicio': return Heart;
      case 'logro': return Award;
      case 'alerta': return Bell;
      default: return Bell;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'meta': return 'text-v-ambar-700 bg-v-ambar-100';
      case 'ejercicio': return 'text-v-rosa-700 bg-v-rosa-100';
      case 'logro': return 'text-v-lima-700 bg-v-lima-100';
      case 'alerta': return 'text-v-error bg-v-error-container';
      default: return 'text-v-on-surface-variant bg-v-background';
    }
  };

  const formatTime = (iso: string) => {
    const fecha = new Date(iso);
    const diffMin = Math.floor((Date.now() - fecha.getTime()) / 60000);

    if (diffMin < 1) return 'Ahora';
    if (diffMin < 60) return `Hace ${diffMin} min`;
    const horas = Math.floor(diffMin / 60);
    if (horas < 24) return `Hace ${horas} h`;
    return fecha.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
  };

  const unreadCount = notifications.filter((n) => !n.leida).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Notificaciones</h1>
          <p className="text-v-on-surface-variant">
            {unreadCount > 0 ? `${unreadCount} notificaciones sin leer` : 'Todas las notificaciones leídas'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Marcar todas como leídas
          </button>
        )}
      </div>

      {/* Notifications List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="spinner h-8 w-8"></div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-12">
          <Bell className="h-12 w-12 text-v-on-surface-variant mx-auto mb-4" />
          <p className="text-v-on-surface-variant">No tienes notificaciones</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => {
            const Icon = getNotificationIcon(notification.tipo);
            const colorClass = getNotificationColor(notification.tipo);

            return (
              <div
                key={notification.id}
                className={`bg-card rounded-xl p-4 border transition-all ${
                  !notification.leida ? 'border-primary/50 bg-primary/5' : ''
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`${colorClass} rounded-full p-3 flex-shrink-0`}>
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className={`font-semibold ${!notification.leida ? 'text-v-on-surface' : 'text-v-on-surface-variant'}`}>
                        {notification.titulo}
                      </h3>
                      <div className="flex items-center space-x-2 ml-2">
                        {!notification.leida && (
                          <button
                            onClick={() => markAsRead(notification.id)}
                            className="p-1 hover:bg-accent rounded transition-colors"
                            title="Marcar como leída"
                          >
                            <Check className="h-4 w-4 text-v-on-surface-variant" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification(notification.id)}
                          className="p-1 hover:bg-accent rounded transition-colors"
                          title="Eliminar"
                        >
                          <X className="h-4 w-4 text-v-on-surface-variant" />
                        </button>
                      </div>
                    </div>
                    {notification.cuerpo && (
                      <p className="text-sm text-v-on-surface-variant mb-2">{notification.cuerpo}</p>
                    )}
                    <div className="flex items-center space-x-2 text-xs text-v-on-surface-variant">
                      <Clock className="h-3 w-3" />
                      <span>{formatTime(notification.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
